// src/components/hr/Performance/HRPerformanceReviewDashboard.jsx

import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Avatar,
  Chip,
  CircularProgress,
  alpha,
  useTheme,
  Paper,
  styled
} from "@mui/material";
import {
  AssignmentTurnedIn,
  HourglassTop,
  RateReview,
  Flag
} from "@mui/icons-material";
import {
  DashboardCustomize, 
  History
} from "@mui/icons-material";
import axios from "axios";
import { localAxiosGet } from "../../../Axios/axios";

import HREmployeeGoalsReview from "./HREmployeeGoalsReview";
import HREmployeeReviewList from "./HREmployeeReviewList";
import GoalHistory from "./GoalHistory";

/* -------------------- TAB STYLES (SAME AS LEAVE DASHBOARD) -------------------- */

const StyledTabs = styled(Tabs)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: 12,
  marginBottom: theme.spacing(3),
  padding: "6px",
  "& .MuiTabs-indicator": {
    height: 0,
  },
  "& .MuiTab-root": {
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: "none",
    minHeight: 48,
    borderRadius: 8,
    margin: "0 4px",
    "&.Mui-selected": {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.primary.light + "20",
    },
  },
}));

const ContentPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 16,
  backgroundColor: theme.palette.background.paper,
}));

/* -------------------- CARD CONFIG -------------------- */

const CARD_METRICS = [
  {
    key: "pending_goals",
    label: "Pending Goals",
    type: "goal",
    icon: HourglassTop,
    color: "#F59E0B",
    lightColor: "#FFFBEB",
    redirectTab: 1
  },
  {
    key: "submitted_goals",
    label: "Submitted for Review Goal",
    type: "goal",
    icon: AssignmentTurnedIn,
    color: "#4F46E5",
    lightColor: "#EEF2FF",
    redirectTab: 1
  },
  {
    key: "pending_appraisals",
    label: "Pending Appraisals",
    type: "appraisal",
    icon: RateReview,
    color: "#8B5CF6",
    lightColor: "#F5F3FF",
    redirectTab: 2
  },
  {
    key: "total_appraisals",
    label: "Total Appraisals",
    type: "appraisal",
    icon: Flag,
    color: "#10B981",
    lightColor: "#ECFDF5",
    redirectTab: 2
  }
];

/* -------------------- MAIN COMPONENT -------------------- */

const HRPerformanceReviewDashboard = () => {
  const theme = useTheme();
  const hr_id = 184;
  const accessToken = localStorage.getItem("accessToken");

  const [activeTab, setActiveTab] = useState(0);
  const [activeCard, setActiveCard] = useState(CARD_METRICS[0]);
  const [stats, setStats] = useState({});
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  /* -------------------- FETCH STATS -------------------- */

  useEffect(() => {
    axios(
      localAxiosGet(`/performanceHr/stats?hr_id=${hr_id}`, accessToken)
    ).then((res) => {
      if (res.data.status === "SUCCESS") {
        setStats(res.data.result);
      }
    });
  }, [hr_id, accessToken]);

  /* -------------------- FETCH TABLE DATA -------------------- */

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios(
        localAxiosGet(`/performanceHr/get_all_goals`)
      );

      let data = res.data.result?.rows || [];

      if (activeCard.key === "pending_goals") {
        data = data.filter((g) => g.status === "Pending");
      }

      if (activeCard.key === "submitted_goals") {
        data = data.filter((g) => g.submit_for_review === true);
      }

      setRows(data);
    } catch {
      setRows([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [activeCard]);

  /* -------------------- DASHBOARD VIEW -------------------- */

  const DashboardView = () => (
    <>
      {/* CARDS */}
      <Box sx={{ mt: 1, mb: 2 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 3
          }}
        >
          {CARD_METRICS.map((metric) => (
            <Box
              key={metric.key}
              onClick={() => setActiveCard(metric)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 1,
                height: 82,
                borderRadius: 2,
                cursor: "pointer",
                background: metric.lightColor,
                border:
                  activeCard.key === metric.key
                    ? `2px solid ${metric.color}`
                    : "1px solid #E5E7EB",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: `0 8px 16px ${alpha(metric.color, 0.15)}`
                }
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: alpha(metric.color, 0.15),
                  color: metric.color
                }}
              >
                <metric.icon sx={{ fontSize: 24 }} />
              </Box>

              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {metric.label}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, color: metric.color }}
                >
                  {stats[metric.key] || 0}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* TABLE */}
      <Box sx={{ background: "#fff", borderRadius: 3 }}>
        <Box px={3} py={2} display="flex" justifyContent="space-between">
          <Typography variant="h6" fontWeight={700}>
            {activeCard.label}
          </Typography>
          <Button onClick={() => setActiveTab(activeCard.redirectTab)}>
            View All →
          </Button>
        </Box>

        {loading ? (
          <Box py={4} textAlign="center">
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead sx={{ background: theme.palette.grey[100] }}>
              <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Designation</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length ? (
                rows.slice(0, 5).map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar>{row.employee.first_name[0]}</Avatar>
                        {row.employee.first_name} {row.employee.last_name}
                      </Box>
                    </TableCell>
                    <TableCell>{row.employee.department_name}</TableCell>
                    <TableCell>{row.employee.designation_name}</TableCell>
                    <TableCell>
                      <Chip size="small" label={row.status} />
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" variant="contained">
                        Review
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No records found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Box>
    </>
  );

  /* -------------------- RENDER -------------------- */

  return (
    <Box sx={{ py: 2, px:1, background: "#F8FAFC", minHeight: "100vh" }}>
      <StyledTabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab
          icon={<DashboardCustomize />}
          iconPosition="top"
          label="Dashboard"
        />
        <Tab
          icon={<Flag />}
          iconPosition="top"
          label="Employee Goals"
        />
        <Tab
          icon={<RateReview />}
          iconPosition="top"
          label="Employee Appraisals"
        />
        <Tab
          icon={<History />}
          iconPosition="top"
          label="Predefined Goals"
        />
      </StyledTabs>

      <ContentPaper>
        {activeTab === 0 && <DashboardView />}
        {activeTab === 1 && <HREmployeeGoalsReview />}
        {activeTab === 2 && <HREmployeeReviewList />}
        {activeTab === 3 && <GoalHistory />}
      </ContentPaper>
    </Box>
  );
};

export default HRPerformanceReviewDashboard;
